import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { ArrowRight, LineChart, Cpu, Megaphone, ClipboardList, Users, Rocket } from "lucide-react";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  useEngineFractionals,
  fractionalRowToCard,
} from "@/lib/engineSurfaces";
import { ProviderCard, type ProviderCardData } from "@/components/services/ProviderCard";
import { FeaturedSpotlight } from "@/components/services/FeaturedSpotlight";
import PillarFAQ, { type FAQItem } from "@/components/shared/PillarFAQ";
import DiscoveryCallModal from "@/components/shared/DiscoveryCallModal";
import BecomeProviderCTA from "@/components/shared/BecomeProviderCTA";

const ROLE_KEYS = ["cfo", "cto", "cmo"] as const;
type RoleKey = typeof ROLE_KEYS[number];
const ROLE_ICON: Record<RoleKey, typeof LineChart> = { cfo: LineChart, cto: Cpu, cmo: Megaphone };

const ROLE_FILTERS = ["ALL", "CFO", "CTO", "CMO", "COO", "CHRO", "CPO"] as const;
type RoleFilter = typeof ROLE_FILTERS[number];

const FAQ_ITEMS: FAQItem[] = [
  {
    question: { en: "What's the typical engagement length?", fr: "Quelle est la durée typique d'un engagement ?" },
    answer: {
      en: "Most fractional engagements run 3-12 months. Some become permanent part-time relationships that last 2+ years. The executive sets the terms with you directly — Bootpreneurs coordinates the initial match and holds payment in escrow; the engagement itself is between you and the executive.",
      fr: "La plupart des engagements fractional durent de 3 à 12 mois. Certains deviennent des relations permanentes à temps partiel qui durent 2 ans ou plus. L'exécutif définit les termes directement avec vous — Bootpreneurs coordonne la mise en relation initiale et détient le paiement en escrow ; l'engagement lui-même est entre vous et l'exécutif.",
    },
  },
  {
    question: { en: "Can we start with just one day per week?", fr: "Peut-on démarrer avec un seul jour par semaine ?" },
    answer: {
      en: "Yes. Most executives on the bench work 1-5 days per week depending on the engagement. The card on each profile shows typical hours/month. Starting at 1 day/week is common for founders who want to test the fit before scaling the engagement.",
      fr: "Oui. La plupart des exécutifs du bench travaillent entre 1 et 5 jours par semaine selon l'engagement. Chaque profil indique les heures/mois typiques. Démarrer à 1 jour/semaine est courant pour les dirigeants qui veulent tester le fit avant de scaler l'engagement.",
    },
  },
  {
    question: {
      en: "What if we need a role not in the CFO/CTO/CMO categories?",
      fr: "Et si nous avons besoin d'un rôle qui n'est pas dans les catégories CFO/CTO/CMO ?",
    },
    answer: {
      en: "Use the filters above to check — the bench includes COO, CHRO, and CPO as well. If we don't have a match for your specific role, book a discovery call and tell us what you need. We either find the right fit in our extended network, or tell you we can't help (and point you elsewhere).",
      fr: "Utilisez les filtres ci-dessus pour vérifier — le bench inclut aussi COO, CHRO, et CPO. Si nous n'avons pas de correspondance pour votre rôle précis, réservez un appel découverte et dites-nous ce dont vous avez besoin. Soit nous trouvons le bon fit dans notre réseau étendu, soit nous vous disons que nous ne pouvons pas vous aider (et nous vous orientons ailleurs).",
    },
  },
  {
    question: { en: "How do you vet fractional executives?", fr: "Comment sélectionnez-vous les exécutifs fractional ?" },
    answer: {
      en: "Every executive on the bench passes three filters: track record (verified P&L-level operator experience, not just titles), references (minimum 2 founder-level references from past engagements), and alignment (they've worked with UEMOA or cross-border founders specifically, or bring directly applicable experience from adjacent markets). Executives who don't stay the bar get removed.",
      fr: "Chaque exécutif du bench passe trois filtres : historique (expérience opérationnelle vérifiée au niveau P&L, pas juste des titres), références (minimum 2 références au niveau dirigeant d'engagements précédents), et alignement (ils ont travaillé spécifiquement avec des dirigeants UEMOA ou cross-border, ou apportent une expérience directement applicable de marchés adjacents). Les exécutifs qui ne maintiennent pas ce niveau sont retirés.",
    },
  },
  {
    question: { en: "What happens if it's not working?", fr: "Que se passe-t-il si ça ne fonctionne pas ?" },
    answer: {
      en: "Tell us. We can coordinate a rematch with another executive on the bench — no additional Bootpreneurs fee. Your direct contract with the first executive is between you, but Bootpreneurs won't charge to help you find a better fit. If nothing on the bench works, we'll be direct: we'll tell you to look elsewhere rather than force a bad match.",
      fr: "Dites-le nous. Nous pouvons coordonner une mise en relation avec un autre exécutif du bench — pas de frais supplémentaires côté Bootpreneurs. Votre contrat direct avec le premier exécutif reste entre vous, mais Bootpreneurs ne facturera pas pour vous aider à trouver un meilleur fit. Si rien sur le bench ne fonctionne, nous serons directs : nous vous dirons d'aller chercher ailleurs plutôt que de forcer un mauvais match.",
    },
  },
  {
    question: {
      en: "Can we convert a fractional engagement to full-time?",
      fr: "Peut-on convertir un engagement fractional en poste à temps plein ?",
    },
    answer: {
      en: "Often, yes — though the decision is between you and the executive. Bootpreneurs doesn't charge conversion fees. If an executive on the bench is the right long-term fit as a full-time hire, we'd rather see that happen cleanly than block it. We occasionally bring them back to the bench when they're available again.",
      fr: "Souvent oui — mais la décision est entre vous et l'exécutif. Bootpreneurs ne facture pas de frais de conversion. Si un exécutif du bench est le bon fit long terme en tant que recrutement à temps plein, nous préférons voir ça arriver proprement que le bloquer. Nous les rappelons occasionnellement dans le bench quand ils redeviennent disponibles.",
    },
  },
];

export default function FractionalPillar() {
  const { t, i18n } = useTranslation();
  const lang: "en" | "fr" = (i18n.language || "en").startsWith("fr") ? "fr" : "en";
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("ALL");
  const [discoveryOpen, setDiscoveryOpen] = useState(false);

  const { data: engineRows = [] } = useEngineFractionals();

  const cards: ProviderCardData[] = useMemo(
    () => engineRows.map(fractionalRowToCard),
    [engineRows],
  );

  const filtered = useMemo(
    () =>
      roleFilter === "ALL"
        ? cards
        : cards.filter((c) => (c.role_label ?? "").toUpperCase().startsWith(roleFilter)),
    [cards, roleFilter],
  );

  const noFilters = roleFilter === "ALL";
  const featured = useMemo(
    () => (noFilters ? filtered.filter((c) => c.featured === true).slice(0, 3) : []),
    [filtered, noFilters],
  );
  const featuredIds = useMemo(() => new Set(featured.map((c) => c.user_id)), [featured]);
  const grid = useMemo(
    () => filtered.filter((c) => !featuredIds.has(c.user_id)),
    [filtered, featuredIds],
  );

  return (
    <PublicShell>
      <Seo title={t("pages.fractional.seoTitle")} description={t("pages.fractional.seoDescription")} />

      {/* Hero CTAs */}
      <section className="border-b border-border">
        <div className="container py-10">
          <div className="flex flex-wrap items-center gap-3">
            <Button asChild size="lg">
              <a href="#fractional-executives">
                {lang === "fr" ? "Voir les profils" : "Browse executives"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Link
              to="/services/coaching"
              className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 ml-4"
            >
              {lang === "fr"
                ? "Pas sûr entre fractional et coaching ? Comparer les options →"
                : "Not sure if you need fractional or coaching? Compare options →"}
            </Link>
          </div>
        </div>
      </section>

      {/* Roles overview */}
      <section className="container py-16 md:py-20">
        <h2 className="mb-10 font-display text-3xl font-bold md:text-4xl">{t("pages.fractional.rolesTitle")}</h2>
        <div className="grid gap-5 md:grid-cols-3">
          {ROLE_KEYS.map((key) => {
            const Icon = ROLE_ICON[key];
            return (
              <Card key={key} className="transition hover:-translate-y-1 hover:border-primary/50">
                <CardContent className="p-6">
                  <div className="mb-4 grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-xl font-bold">{t(`pages.fractional.roles.${key}.title`)}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{t(`pages.fractional.roles.${key}.scope`)}</p>
                  <p className="mt-4 text-sm font-semibold">{t(`pages.fractional.roles.${key}.from`)}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Executives directory */}
      <section id="fractional-executives" className="border-y border-border bg-muted/30">
        <div className="container py-16 md:py-20">
          <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <h2 className="font-display text-3xl font-bold md:text-4xl">
                {lang === "fr" ? "Dirigeant·es disponibles" : "Available executives"}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {lang === "fr"
                  ? "8 dirigeant·es expérimenté·es, principalement basé·es en zone UEMOA."
                  : "8 senior executives, primarily based across UEMOA."}
              </p>
            </div>
          </div>

          <div className="mb-6 flex flex-wrap gap-2">
            {ROLE_FILTERS.map((r) => (
              <button
                key={r}
                onClick={() => setRoleFilter(r)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  roleFilter === r
                    ? "bg-primary text-primary-foreground"
                    : "bg-background text-muted-foreground hover:bg-muted"
                }`}
              >
                {r === "ALL" ? (lang === "fr" ? "Tous" : "All") : r}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-background p-8 text-center text-sm text-muted-foreground">
              {lang === "fr" ? "Aucun profil correspondant." : "No matching profiles."}
            </div>
          ) : (
            <>
              {featured.length > 0 && (
                <div className="mb-10">
                  <div className="mb-4">
                    <div className="font-mono text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                      {lang === "fr" ? "À l'honneur" : "Featured"}
                    </div>
                    <h3 className="mt-1 font-display text-2xl font-bold tracking-tight md:text-3xl">
                      {lang === "fr" ? "Sélection de l'équipe" : "Editor's picks"}
                    </h3>
                  </div>
                  <FeaturedSpotlight items={featured} />
                </div>
              )}
              {featured.length > 0 && grid.length > 0 && (
                <h3 className="mb-4 font-display text-xl font-bold tracking-tight">
                  {lang === "fr" ? "Tou·tes les dirigeant·es" : "All executives"}
                </h3>
              )}
              {grid.length > 0 && (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {grid.map((c) => <ProviderCard key={c.user_id} p={c} />)}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* How it works */}
      <section className="container py-16 md:py-20">
        <h2 className="mb-10 font-display text-3xl font-bold md:text-4xl">{t("pages.fractional.howTitle")}</h2>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { Icon: ClipboardList, key: "brief" },
            { Icon: Users, key: "match" },
            { Icon: Rocket, key: "start" },
          ].map(({ Icon, key }) => (
            <div key={key} className="rounded-xl border border-border bg-card p-6">
              <div className="mb-4 grid h-11 w-11 place-items-center rounded-xl bg-accent/15 text-accent">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-lg font-semibold">{t(`pages.fractional.steps.${key}.title`)}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{t(`pages.fractional.steps.${key}.desc`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <PillarFAQ lang={lang} items={FAQ_ITEMS} />

      {/* Final CTA */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">
          {lang === "fr" ? "Prêt à rencontrer un opérateur senior ?" : "Ready to meet a senior operator?"}
        </h2>
        <p className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
          {lang === "fr"
            ? "Parcourez les 8 exécutifs ci-dessus, ou réservez un appel découverte de 20 minutes et nous vous mettons en relation avec le bon sous 5 jours."
            : "Browse the 8 executives above, or book a 20-minute discovery call and we'll match you to the right one within 5 days."}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button
            size="lg"
            onClick={() => {
              const grid = document.getElementById("fractional-executives");
              grid?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="min-w-[200px]"
          >
            {lang === "fr" ? "Parcourir les exécutifs" : "Browse executives"}
          </Button>
          <button
            onClick={() => setDiscoveryOpen(true)}
            className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4"
          >
            {lang === "fr" ? "Réserver un appel découverte" : "Book discovery call"}
          </button>
        </div>
      </section>

      <DiscoveryCallModal
        open={discoveryOpen}
        onOpenChange={setDiscoveryOpen}
        sourcePillar="fractional"
        lang={lang}
      />
      <div className="container mx-auto px-4">
        <BecomeProviderCTA kind="fractional" />
      </div>
    </PublicShell>
  );
}
